import React, { useState } from "react";
import { Theme, StandardProps, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import { StyleClassKey } from "js/react/typescript";
import clsx from "clsx";
import defaultImage from "images/default-image.png";
import { MediaContentRendererProps } from "js/react/scenes/components/MediaManagement/Types";
import { MimeType } from "js/models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Checkbox from "@material-ui/core/Checkbox";
import { MoreActionMenu } from "js/react/scenes/components/MediaManagement/MoreActionMenu";
import { CreativePreviewDialog } from "./CreativePreviewDialog";
import { getImageThumbnail } from "js/services/Media";

const styles = (theme: Theme) =>
    createStyles({
        main: {
            position: "relative",
            flexGrow: 1,
            width: "100%",
            "&$folder": {
                cursor: "pointer"
            },
            "&:after": {
                content: ` `,
                position: "absolute",
                top: 0,
                right: 0,
                height: 265,
                left: 0,
                bottom: 0,
                boxShadow: `0px 0px 75px black inset`
            }
        },
        checked: {},
        root: {
            display: "flex",
            width: "100%",
            position: "relative",
            paddingTop: "100%",
            margin: theme.spacing(0.5, 0.25),
            "&$checked": {
                "& $videoTitle": {
                    textDecoration: "underline"
                }
            },
            "&:after": {
                content: `""`,
                position: "absolute",
                top: 0,
                right: 0,
                height: 265,
                left: 0,
                bottom: 0,
                boxShadow: `0px 0px 75px black inset`
            }
        },
        iconWrapper: {
            background: "gray",
            padding: 4,
            opacity: 0.5
        },
        videoTitle: {
            zIndex: 1,
            paddingTop: theme.spacing(0.5),
            fontSize: "0.9rem",
            textAlign: "center",

            cursor: "pointer",
            color: theme.palette.common.black,
            width: "inherit",
            paddingLeft: theme.spacing(2),
            opacity: 1,
            transition: theme.transitions.create("opacity", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen
            }),

            //clip line after 2 lines

            display: "-webkit-box",
            "-webkit-box-orient": "vertical",
            "-webkit-line-clamp": "2",
            overflow: "hidden",
            wordBreak: "break-all"
        },
        content: {
            position: "absolute",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0
        },
        hideTitle: {
            opacity: 0
        },
        imgView: {
            width: "100%",
            height: "265px",
            objectFit: "cover",
            objectPosition: "center"
        },
        folderIcon: {
            padding: "50px",
            color: "white",
            width: "100%!important",
            height: "265px",
            background: theme.palette.primary.main
        },
        folder: {},
        checkbox: {
            position: "absolute",
            color: theme.palette.common.white,
            top: "8px",
            padding: 0,
            left: "16px",
            fontSize: "18px",
            zIndex: 3
        },

        externalLinkIcon: {
            color: "white",
            position: "absolute",
            top: "12px",
            fontSize: "18px",
            right: "16px",
            zIndex: 2,
            "&:hover": {
                cursor: "pointer"
            }
        },
        moreVertIcon: {
            color: "white",
            position: "absolute",
            bottom: "12px",
            padding: 0,
            fontSize: "25px",
            right: "16px",
            zIndex: 2,
            "&:hover": {
                cursor: "pointer"
            }
        },
        filmIcon: {
            color: "white",
            position: "absolute",
            bottom: "12px",
            fontSize: "25px",
            left: "16px",
            zIndex: 2
        },
        imageIcon: {
            color: "white",
            position: "absolute",
            bottom: "12px",
            fontSize: "25px",
            left: "16px",
            zIndex: 2
        },
        displayNameWrapper: {
            textAlign: "center",
            "&:hover": {
                "& span": {
                    //display: "inline"
                    overflow: "visible",
                    display: "block"
                }
            }
        }
    });

type MediaGridComponentClassKey = StyleClassKey<typeof styles>;

const useStyles = makeStyles(styles);

export interface MediaGridComponentProps
    extends StandardProps<{}, MediaGridComponentClassKey>,
        MediaContentRendererProps {}

function Component(props: MediaGridComponentProps) {
    const classes = useStyles(props);
    const { onChosen, isChecked, onChecked, media } = props;
    const [showTitle, setShowTitle] = useState(true);
    const [previewToggle, setPreviewToggle] = useState(false);

    const onClosePreview = () => {
        setPreviewToggle(false);
    };

    return (
        <div
            className={clsx(classes.root, {
                [classes.checked]: isChecked
            })}
        >
            <div
                className={classes.content}
                onClick={() => onChecked(media, !isChecked)}
            >
                <div>
                    {previewToggle && (
                        <CreativePreviewDialog
                            onClose={onClosePreview}
                            title={media.DisplayName}
                            content={media.detail}
                        />
                    )}
                </div>

                <React.Fragment>
                    <div className={clsx(classes.main)}>
                        {media.detail.mimeType !== null && (
                            <FontAwesomeIcon
                                icon={["fas", "external-link"]}
                                className={classes.externalLinkIcon}
                                onClick={() => {
                                    setPreviewToggle(true);
                                    // console.log(media);
                                }}
                            />
                        )}
                        <MoreActionMenu
                            onChosen={props.onChosen}
                            onDelete={props.onDelete}
                            selectionType={props.selectionType}
                            media={props.media}
                            className={classes.moreVertIcon}
                            onRename={props.onRename}
                            showPreviewIcon={false}
                        />
                        <div>
                            <Checkbox
                                className={classes.checkbox}
                                color={"default"}
                                checked={isChecked}
                            />
                        </div>

                        {media.type == "file" && (
                            <React.Fragment>
                                {media.detail.mimeType === MimeType.GIF ||
                                media.detail.mimeType === MimeType.JPEG ||
                                media.detail.mimeType === MimeType.PNG ? (
                                    <FontAwesomeIcon
                                        icon={["fas", "image"]}
                                        className={classes.imageIcon}
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={["fas", "film"]}
                                        className={classes.imageIcon}
                                    />
                                )}

                                {
                                    <img
                                        className={classes.imgView}
                                        onError={e =>
                                            ((e.target as any).src = defaultImage)
                                        }
                                        src={
                                            media.thumbnailUrl
                                                ? getImageThumbnail(
                                                      media.thumbnailUrl
                                                  )
                                                : defaultImage
                                        }
                                        alt={media.DisplayName}
                                    />
                                }
                            </React.Fragment>
                        )}
                        {media.detail.mimeType == null && (
                            <FontAwesomeIcon
                                onClick={() => {
                                    props.onItemClick(media);
                                }}
                                icon={["fas", "folder"]}
                                className={classes.folderIcon}
                            />
                        )}
                    </div>

                    <div className={classes.displayNameWrapper}>
                        <Typography
                            variant="caption"
                            className={clsx(classes.videoTitle, {
                                [classes.hideTitle]: !showTitle
                            })}
                            onClick={() => {
                                if (props.selectionType === media.type) {
                                    onChosen(media);
                                }
                            }}
                        >
                            {media.DisplayName}
                        </Typography>
                    </div>
                </React.Fragment>
            </div>
        </div>
    );
}

Component.defaultProps = {} as MediaGridComponentProps;
Component.displayName = "MediaGridComponent";
export const MediaGridComponent = Component;
export default MediaGridComponent;
